import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import useLeadPipelineConfig from '../../hooks/useLeadPipelineConfig';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ─── Lead-level sales pipeline + funnel fields, surfaced on ContactDetail.
// Self-contained: auto-saves through the right endpoint per field (dedicated
// endpoints for stage / coach / sales-outcome; generic PUT for simple fields).
// Built ALONGSIDE the Deal pipeline — this writes to the Contact/lead record.

const BAND_CLS = { green: 'bg-emerald-100 text-emerald-700', amber: 'bg-amber-100 text-amber-700', red: 'bg-red-100 text-red-700' };
const BIC_CLS = { action_needed: 'bg-amber-100 text-amber-700', waiting_on_lead: 'bg-blue-100 text-blue-700', na: 'bg-stone-100 text-stone-500' };
const SALES_OUTCOMES = ['Call Completed', 'No Show'];

const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const selectCls = "mt-0.5 w-full text-sm text-stone-800 bg-white border border-stone-200 hover:border-stone-300 focus:border-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/10 rounded-md px-2 py-1.5";
const inputCls = selectCls;

function Card({ title, children }) {
    return (
        <div className="bg-white rounded-xl border border-stone-200">
            <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-stone-100">
                <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
            </div>
            <div className="p-4 sm:p-5">{children}</div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <p className="text-xs text-stone-400 mb-0.5">{label}</p>
            {children}
        </div>
    );
}

// Select that always includes the current value (even if it's not in the
// configured option list), plus an "(unset)" choice.
function SelectField({ label, value, options, onSave, disabled }) {
    const opts = [...options];
    if (value && !opts.includes(value)) opts.unshift(value);
    return (
        <Field label={label}>
            <select className={selectCls} value={value || ''} disabled={disabled}
                onChange={e => onSave(e.target.value || null)}>
                <option value="">(unset)</option>
                {opts.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        </Field>
    );
}

// Text/number field that saves on blur only when changed.
function TextField({ label, value, onSave, type = 'text', disabled, placeholder }) {
    const [draft, setDraft] = useState(value ?? '');
    useEffect(() => { setDraft(value ?? ''); }, [value]);
    const commit = () => {
        const norm = draft === '' ? null : (type === 'number' ? Number(draft) : draft);
        const cur = value ?? (type === 'number' ? null : '');
        if (String(norm ?? '') !== String(cur ?? '')) onSave(norm);
    };
    return (
        <Field label={label}>
            <input type={type} className={inputCls} value={draft} disabled={disabled} placeholder={placeholder}
                onChange={e => setDraft(e.target.value)} onBlur={commit}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} />
        </Field>
    );
}

function Badge({ children, cls }) {
    return <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${cls || 'bg-stone-100 text-stone-600'}`}>{children}</span>;
}

export default function LeadPipelineCard({ contact, contactId, teamMembers = [], getAuthHeader, onChange }) {
    const { config } = useLeadPipelineConfig();
    const [busy, setBusy] = useState(false);
    const [products, setProducts] = useState([]);

    const stages = config.stages || [];
    const opt = (k) => config[k] || [];

    useEffect(() => {
        // Product picker for "Product Sold" (graceful if unavailable).
        axios.get(`${API}/api/products/picker`, { headers: getAuthHeader() })
            .then(res => setProducts(Array.isArray(res.data) ? res.data : (res.data?.data || [])))
            .catch(() => setProducts([]));
    }, [getAuthHeader]);

    const run = useCallback(async (fn) => {
        setBusy(true);
        try { await fn(); if (onChange) await onChange(); }
        catch (err) { toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Update failed'); }
        finally { setBusy(false); }
    }, [onChange]);

    const patch = (fields) => run(() => axios.put(`${API}/api/contacts/${contactId}`, fields, { headers: getAuthHeader() }));
    const setStage = (stageKey) => run(() => axios.put(`${API}/api/contacts/${contactId}/pipeline-stage`, { stage: stageKey }, { headers: getAuthHeader() }));
    const setCoach = (userId) => run(() => axios.put(`${API}/api/contacts/${contactId}/assign-coach`, { coach_user_id: userId || null }, { headers: getAuthHeader() }));
    const setOutcome = (outcome) => run(() => axios.put(`${API}/api/contacts/${contactId}/sales-outcome`, { outcome: outcome || null }, { headers: getAuthHeader() }));

    const onProductSold = (val) => {
        if (val === '__custom__') { patch({ product_sold_id: null, product_sold_name: 'Custom' }); return; }
        const p = products.find(x => (x.product_id || x.id) === val);
        if (!p) { patch({ product_sold_id: null, product_sold_name: null, product_value: null, product_value_currency: null }); return; }
        patch({
            product_sold_id: p.product_id || p.id,
            product_sold_name: p.name,
            product_value: p.amount ?? p.price ?? null,
            product_value_currency: p.currency || null,
        });
    };

    const c = contact || {};
    const currentStageKey = (stages.find(s => s.key === c.pipeline_stage || s.label === c.pipeline_stage) || {}).key || c.pipeline_stage || '';

    return (
        <div className="space-y-6">
            {/* ── Sales Pipeline ───────────────────────────────────────────── */}
            <Card title="Sales Pipeline">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
                    {/* Stage */}
                    <Field label="Pipeline Stage">
                        <select className={selectCls} value={currentStageKey} disabled={busy}
                            onChange={e => setStage(e.target.value)}>
                            <option value="">(unset)</option>
                            {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                    </Field>
                    {/* Time in stage (computed, read-only) */}
                    <Field label="Time in Stage">
                        {c.time_in_stage_days != null
                            ? <Badge cls={BAND_CLS[c.time_in_stage_band]}>{c.time_in_stage_days} day{c.time_in_stage_days === 1 ? '' : 's'}</Badge>
                            : <span className="text-sm text-stone-400">—</span>}
                    </Field>
                    {/* Ball in court (computed, read-only) */}
                    <Field label="Ball in Court">
                        {c.ball_in_court
                            ? <Badge cls={BIC_CLS[c.ball_in_court]}>{c.ball_in_court_label || humanize(c.ball_in_court)}</Badge>
                            : <span className="text-sm text-stone-400">—</span>}
                    </Field>
                    {/* Coach assigned (reuses org team-member list; stores user_id) */}
                    <Field label="Coach Assigned">
                        <select className={selectCls} value={c.coach_assigned_user_id || ''} disabled={busy}
                            onChange={e => setCoach(e.target.value)}>
                            <option value="">(unassigned)</option>
                            {teamMembers.map(m => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
                        </select>
                    </Field>
                    <SelectField label="Prospect Source" value={c.prospect_source} options={opt('prospect_source_options')} disabled={busy} onSave={v => patch({ prospect_source: v })} />
                    <SelectField label="Prospect Qualification" value={c.prospect_qualification} options={opt('prospect_qualification_options')} disabled={busy} onSave={v => patch({ prospect_qualification: v })} />
                    <SelectField label="Prospect Status" value={c.prospect_status} options={opt('prospect_status_options')} disabled={busy} onSave={v => patch({ prospect_status: v })} />
                    <Field label="Lead Status (system)">
                        {c.lead_status ? <Badge>{humanize(c.lead_status)}</Badge> : <span className="text-sm text-stone-400">—</span>}
                    </Field>
                    <div className="sm:col-span-2 xl:col-span-3">
                        <TextField label="Last Touchpoint" value={c.last_touchpoint} disabled={busy} placeholder="e.g. Email received 3 days ago" onSave={v => patch({ last_touchpoint: v })} />
                    </div>
                </div>

                {/* Sales Call */}
                <div className="mt-5 pt-4 border-t border-stone-100">
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Sales Call</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
                        <TextField label="Sales Call Date" type="date" value={(c.sales_call_date || '').slice(0, 10)} disabled={busy} onSave={v => patch({ sales_call_date: v })} />
                        <Field label="Sales Call Outcome">
                            <select className={selectCls} value={c.sales_call_outcome || ''} disabled={busy}
                                onChange={e => setOutcome(e.target.value)}>
                                <option value="">(none)</option>
                                {SALES_OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </Field>
                        <TextField label="Call Count" type="number" value={c.call_count} disabled={busy} onSave={v => patch({ call_count: v })} />
                    </div>
                </div>

                {/* Won */}
                <div className="mt-5 pt-4 border-t border-stone-100">
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Won</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
                        {products.length > 0 ? (
                            <Field label="Product Sold">
                                <select className={selectCls} value={c.product_sold_id || (c.product_sold_name === 'Custom' ? '__custom__' : '')} disabled={busy}
                                    onChange={e => onProductSold(e.target.value)}>
                                    <option value="">(none)</option>
                                    {products.map(p => <option key={p.product_id || p.id} value={p.product_id || p.id}>{p.name}</option>)}
                                    <option value="__custom__">Custom…</option>
                                </select>
                            </Field>
                        ) : (
                            <TextField label="Product Sold" value={c.product_sold_name} disabled={busy} onSave={v => patch({ product_sold_name: v })} />
                        )}
                        <TextField label="Product Value" type="number" value={c.product_value} disabled={busy} onSave={v => patch({ product_value: v })} />
                        <TextField label="Currency" value={c.product_value_currency} disabled={busy} placeholder="USD" onSave={v => patch({ product_value_currency: v })} />
                    </div>
                </div>

                {/* Lost */}
                <div className="mt-5 pt-4 border-t border-stone-100">
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Lost</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
                        <SelectField label="Reason Lost" value={c.reason_lost} options={opt('reason_lost_options')} disabled={busy} onSave={v => patch({ reason_lost: v })} />
                        {c.reason_lost === 'Other' && (
                            <div className="sm:col-span-2">
                                <TextField label="Reason (Other)" value={c.reason_lost_other} disabled={busy} onSave={v => patch({ reason_lost_other: v })} />
                            </div>
                        )}
                    </div>
                </div>
            </Card>

            {/* ── Campaign & Funnel ────────────────────────────────────────── */}
            <Card title="Campaign & Funnel">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
                    <SelectField label="Funnel" value={c.funnel} options={opt('funnel_options')} disabled={busy} onSave={v => patch({ funnel: v })} />
                    <SelectField label="Page Variant" value={c.page_variant} options={opt('page_variant_options')} disabled={busy} onSave={v => patch({ page_variant: v })} />
                    <SelectField label="Form Variant" value={c.form_variant} options={opt('form_variant_options')} disabled={busy} onSave={v => patch({ form_variant: v })} />
                    <SelectField label="Product Variant" value={c.product_variant} options={opt('product_variant_options')} disabled={busy} onSave={v => patch({ product_variant: v })} />
                </div>
            </Card>

            {/* ── UTM Acquisition (read-only; set by the funnel form) ───────── */}
            <Card title="UTM Acquisition">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
                    {['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].map(k => (
                        <Field key={k} label={humanize(k.replace('utm_', 'UTM '))}>
                            <p className="text-sm text-stone-800 font-medium break-words">{c[k] || <span className="text-stone-400">—</span>}</p>
                        </Field>
                    ))}
                </div>
                <p className="text-xs text-stone-400 mt-3">Captured automatically from the funnel link when the lead's form loads.</p>
            </Card>
        </div>
    );
}
